// CastSimReport.tsx — Pick sequence simulation comparison report.
// Modal overlay showing algorithm × metric table with multi-criteria scoring.

import { useState } from 'react';
import type { SimulationResult, MonteCarloStats, SimMetrics } from '../types/panelvar';

interface Props {
  results:   SimulationResult[];
  mcStats:   MonteCarloStats;
  isRunning: boolean;
  onApply:   (pickOrder: string[]) => void;
  onClose:   () => void;
}

type MetricKey = keyof SimMetrics;

const METRIC_DEFS: { key: MetricKey; label: string; unit: string; lowerIsBetter: boolean }[] = [
  { key: 'totalArmRotationRad',    label: 'Arm rotation',   unit: 'rad',  lowerIsBetter: true },
  { key: 'totalArmExtensionDelta', label: 'Arm extension',  unit: 'in',   lowerIsBetter: true },
  { key: 'mobilizationCount',      label: 'Crane moves',    unit: '',     lowerIsBetter: true },
  { key: 'rigChangeCount',         label: 'Rig changes',    unit: '',     lowerIsBetter: true },
  { key: 'maxPickRadius',          label: 'Max radius',     unit: 'in',   lowerIsBetter: true },
  { key: 'totalWeightTons',        label: 'Total weight',   unit: 'T',    lowerIsBetter: false },
  { key: 'criticalPickCount',      label: 'Critical picks', unit: '',     lowerIsBetter: true },
  { key: 'totalSwingDistIn',       label: 'Swing dist',     unit: 'ft',   lowerIsBetter: true },
];

function fmt(v: number, unit: string): string {
  if (unit === 'rad') return v.toFixed(2);
  if (unit === 'in')  return Math.round(v).toLocaleString();
  if (unit === 'ft')  return Math.round(v / 12).toLocaleString();
  if (unit === 'T')   return v.toFixed(1);
  return String(Math.round(v));
}

function fmtMcCell(median: number, iqr: number, unit: string): string {
  return `${fmt(median, unit)} ±${fmt(iqr, unit)}`;
}

export default function CastSimReport({ results, mcStats, isRunning, onApply, onClose }: Props) {
  const [sortCol, setSortCol]           = useState<MetricKey | null>(null);
  const [sortDir, setSortDir]           = useState<1 | -1>(1);
  const [checkedMetrics, setCheckedMetrics] = useState<Set<MetricKey>>(
    new Set(['totalArmRotationRad', 'rigChangeCount']),
  );

  function toggleMetric(k: MetricKey) {
    setCheckedMetrics(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  }

  function handleSort(k: MetricKey) {
    if (sortCol === k) setSortDir(d => (d === 1 ? -1 : 1) as 1 | -1);
    else { setSortCol(k); setSortDir(1); }
  }

  // Compute multi-criteria rank scores for deterministic algorithms
  const deterministic = results.filter(r => r.pickOrder.length > 0);

  function rankScore(r: SimulationResult): number {
    if (checkedMetrics.size === 0) return 0;
    let score = 0;
    for (const k of checkedMetrics) {
      const def = METRIC_DEFS.find(d => d.key === k)!;
      const vals = deterministic.map(x => x.metrics[k]).sort((a, b) => a - b);
      const rank = vals.indexOf(r.metrics[k]) + 1;
      score += def.lowerIsBetter ? rank : (deterministic.length + 1 - rank);
    }
    return score;
  }

  const scores = new Map(deterministic.map(r => [r.algorithmId, rankScore(r)]));
  const bestScore = Math.min(...Array.from(scores.values()));

  // Sort displayed rows
  const displayRows = results.slice();
  if (sortCol) {
    displayRows.sort((a, b) => {
      const av = a.metrics[sortCol];
      const bv = b.metrics[sortCol];
      return (av - bv) * sortDir;
    });
  }

  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  };

  const modal: React.CSSProperties = {
    background: '#1e2534', border: '1px solid #2a3a4a', borderRadius: 10,
    width: 'min(98vw, 860px)', maxHeight: '90vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  };

  const th: React.CSSProperties = {
    padding: '6px 8px', background: '#151c28', color: '#aac',
    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    cursor: 'pointer', userSelect: 'none', position: 'sticky', top: 0,
  };

  const td: React.CSSProperties = {
    padding: '5px 8px', fontSize: 12, color: '#ccc',
    borderBottom: '1px solid #1a2230', textAlign: 'right',
  };

  const tdLabel: React.CSSProperties = {
    ...td, textAlign: 'left', fontWeight: 600, color: '#dde',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid #2a3a4a' }}>
          <span style={{ flex: 1, fontSize: 14, fontWeight: 700, color: '#eee' }}>Pick Sequence Simulation</span>
          {isRunning && <span style={{ fontSize: 11, color: '#aaa', marginRight: 12 }}>Running…</span>}
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>
            ×
          </button>
        </div>

        {/* Metric toggles */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 14px', borderBottom: '1px solid #1a2230' }}>
          <span style={{ fontSize: 11, color: '#888', alignSelf: 'center' }}>Score by:</span>
          {METRIC_DEFS.map(d => (
            <label key={d.key} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontSize: 11, color: checkedMetrics.has(d.key) ? '#4da6ff' : '#666' }}>
              <input
                type="checkbox"
                checked={checkedMetrics.has(d.key)}
                onChange={() => toggleMetric(d.key)}
                style={{ cursor: 'pointer' }}
              />
              {d.label}
            </label>
          ))}
        </div>

        {/* Table */}
        <div style={{ overflowY: 'auto', flex: 1 }}>
          {isRunning && results.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: '#888', fontSize: 13 }}>
              Computing 500 iterations…
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: 'left' }}>Algorithm</th>
                  {METRIC_DEFS.map(d => (
                    <th key={d.key} style={th} onClick={() => handleSort(d.key)}>
                      {d.label}{d.unit ? ` (${d.unit})` : ''}
                      {sortCol === d.key ? (sortDir === 1 ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                  <th style={th}>Score</th>
                  <th style={th}>Apply</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map(r => {
                  const isMc    = r.algorithmId === 'random';
                  const score   = scores.get(r.algorithmId) ?? null;
                  const isBest  = !isMc && score === bestScore && checkedMetrics.size > 0;
                  const rowBg   = isBest ? 'rgba(68,187,102,0.08)' : undefined;
                  return (
                    <tr key={r.algorithmId} style={{ background: rowBg }}>
                      <td style={{ ...tdLabel, color: isBest ? '#44bb66' : '#dde' }}>
                        {r.label}
                        {isBest && ' ★'}
                      </td>
                      {METRIC_DEFS.map(d => {
                        if (isMc) {
                          return (
                            <td key={d.key} style={{ ...td, color: '#888', fontStyle: 'italic' }}>
                              {fmtMcCell(mcStats.median[d.key], mcStats.iqr[d.key], d.unit)}
                            </td>
                          );
                        }
                        return (
                          <td key={d.key} style={td}>
                            {fmt(r.metrics[d.key], d.unit)}
                          </td>
                        );
                      })}
                      <td style={{ ...td, color: isBest ? '#44bb66' : '#aaa' }}>
                        {isMc ? '—' : (score ?? '—')}
                      </td>
                      <td style={td}>
                        {!isMc && (
                          <button
                            onClick={() => { onApply(r.pickOrder); }}
                            style={{
                              background: isBest ? '#1a4a2a' : '#1a2a3a',
                              border: `1px solid ${isBest ? '#44bb66' : '#3a5a7a'}`,
                              borderRadius: 4,
                              color: isBest ? '#44bb66' : '#4da6ff',
                              cursor: 'pointer',
                              fontSize: 10,
                              padding: '2px 6px',
                            }}>
                            Apply
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 14px', borderTop: '1px solid #2a3a4a', fontSize: 10, color: '#556', textAlign: 'right' }}>
          Score = sum of per-metric ranks (lower = better). ★ = lowest combined score for checked metrics.
        </div>
      </div>
    </div>
  );
}
